import './AcknowledgeOmsChanges.scss';

import { Button, Tab, TabNavigation } from '@hulu/react-style-components';
import { Button as AntButton } from 'antd';
import { lowerCase as _lowerCase } from 'lodash';
import { bem } from 'mission-control-common-components';
import { useMemo, useState } from 'react';

import type {
  AcknowledgeOmsChangesProps,
  UseNotificationsChangeLogResult,
  Variables,
} from '../../../../../../common/NotificationsChangeLog';
import NotificationsChangeLog from '../../../../../../common/NotificationsChangeLog';
import ExpandCollapse from '../../../../../../common/NotificationsChangeLog/ExpandCollapse/ExpandCollapse';
import { useNotifcationsChangeLog } from '../../../../../../common/NotificationsChangeLog/hooks';
import type { AcknowledgeOmsChangesTableName } from '../../../../../../constants';
import { ACKNOWLEDGE_OMS_CHANGES_TABLE } from '../../../../../../constants';
import TableNavigationActionButtons from '../../../../TableNavigationActionButtons';
import { GET_NOTIFICATIONS } from '../hooks/queries';

const [block, element] = bem('acknowledge-oms-changes');

const AcknowledgeOmsChanges = ({ name, id, onCancel }: AcknowledgeOmsChangesProps): JSX.Element => {
  const [selectedTab, setSelectedTab] = useState<AcknowledgeOmsChangesTableName>(ACKNOWLEDGE_OMS_CHANGES_TABLE[0]);

  const variables: Variables = useMemo(() => {
    return {
      filterOptions: {
        campaignIdList: [id],
        isAcknowledged: false,
      },
      name,
      selectedTab,
    };
  }, [id, name, selectedTab]);

  const {
    loading,
    toggles,
    toggleAt,
    toggleAll,
    filteredLogsToShow,
    toggleCheckedAt,
    toggleCheckedAll,
    checkedIds,
    hasMore,
    onNext,
    eventCounts,
    submitAcknowledgements,
    isSubmitting,
  }: UseNotificationsChangeLogResult = useNotifcationsChangeLog({
    GET_NOTIFICATIONS,
    variables,
  });

  const tableNamesToTabs = (tableName: AcknowledgeOmsChangesTableName): JSX.Element => {
    const handleInteraction = (e: React.MouseEvent | React.KeyboardEvent): void => {
      e.stopPropagation();
      e.preventDefault();
      if (e.type === 'click' || (e.type === 'keydown' && (e as React.KeyboardEvent).key === 'Enter')) {
        setSelectedTab(tableName);
      }
    };

    const count = eventCounts?.[tableName] ?? 0;
    return (
      <Tab
        key={tableName}
        title={tableName}
        element={
          <span
            onClick={(e) => handleInteraction(e)}
            onKeyDown={(e) => handleInteraction(e)}
            role="tab"
            aria-label={selectedTab}
            data-testid={`acknowledge-oms-tab-${tableName}`}
          >
            {`${_lowerCase(tableName)}`}&nbsp;({count})<span className={'rows-count'} data-testid="rows-count"></span>
          </span>
        }
        className={element('table-content')}
      >
        <NotificationsChangeLog
          loading={loading}
          toggles={toggles}
          toggleAt={toggleAt}
          filteredLogsToShow={filteredLogsToShow}
          toggleCheckedAt={toggleCheckedAt}
          toggleCheckedAll={toggleCheckedAll}
          checkedIds={checkedIds}
          name={name}
          onNext={onNext}
          hasMore={hasMore}
        />
      </Tab>
    );
  };

  const actionButtonsConfig = useMemo(() => {
    const areAllExpanded = toggles && toggles.length > 0 && toggles.every((t) => t === true);
    return [
      {
        id: 'expand-collapse',
        tooltipClassName: element('tooltip'),
        tooltipLabel: 'Expand/Collapse All',
        isShown: true,
        component: (
          <ExpandCollapse
            onExpandAll={() => toggleAll && toggleAll(true)}
            onCollapseAll={() => toggleAll && toggleAll(false)}
            areAllExpanded={areAllExpanded}
          />
        ),
      },
    ];
  }, [toggles, toggleAll]);

  return (
    <div data-testid="acknowledge-oms-content" className={block()}>
      <div className={element('header')}>Acknowledge OMS Changes</div>
      <div className={element('info')}>
        Changes from OMS have been pushed for [{name}]. Please select which changes to acknowledge.
      </div>
      <div className={element('acknowledge-tab-navigation-wrapper')}>
        <TableNavigationActionButtons config={actionButtonsConfig} />
        <TabNavigation selectedTab={selectedTab} onTabClick={setSelectedTab}>
          {ACKNOWLEDGE_OMS_CHANGES_TABLE.map(tableNamesToTabs)}
        </TabNavigation>
      </div>
      <div className={element('footer')} key="modal-footer">
        <AntButton key="cancel-button" prefixCls={`ant-btn ${element('cancel-button')}`} onClick={onCancel}>
          <span data-testid="cancel-button">Cancel</span>
        </AntButton>
        <Button
          key="acknowledge-button"
          aria-label="acknowledge changes"
          data-testid="acknowledge-button"
          className={element('acknowledge-button')}
          tabIndex={0}
          onClick={() => submitAcknowledgements({ onSuccess: onCancel })}
          loading={isSubmitting}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default AcknowledgeOmsChanges;
